import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'

import { usePageScrollToTop } from '@/components/sidebar'
import { type Claim } from '../../../../models/claim'
import { useCurrentUser } from '../../../../models/user'
import { ClaimIncidentDetailsForm } from './ClaimIncidentDetailsForm'
import { ClaimOthersInvolvedForm } from './ClaimOthersInvolvedForm'
import { ClaimPhotosForm } from './ClaimPhotosForm'
import { ClaimReviewForm } from './ClaimReviewForm'
import { ClaimStepper } from './ClaimStepper'
import { ClaimTruckDriverForm } from './ClaimTruckDriverForm'

interface ClaimFormProps {
  /** Existing claim to edit; omit for a brand-new claim. */
  claim?: Claim
  /** On-behalf org scope (CAI user acting for a customer) — used on every request. */
  orgId?: string
  /** On-behalf target user (create only) — sets created_by to the business owner. */
  userId?: string
  /** Reporter prefill for a new claim (ignored when `claim` is provided). */
  reporterName?: string
  reporterPhone?: string
}

export function ClaimForm({ claim, orgId, userId, reporterName, reporterPhone }: ClaimFormProps) {
  const { user } = useCurrentUser({ enabled: true })
  const [claimId, setClaimId] = useState<string | undefined>(claim?.id)
  const [activeStep, setActiveStep] = useState(1)
  // The latest saved claim, refreshed from each step's PATCH response. Every step owns
  // its own react-hook-form instance and unmounts when inactive, so on Back it must
  // re-hydrate its defaults from this instead of resetting to empty. (Step 5 fetches
  // the claim itself via useClaim.)
  const [claimData, setClaimData] = useState<Claim | undefined>(claim)

  // Jump to the top on every Next/Back so each step opens at its heading rather than
  // wherever the previous step's button left the scroll position.
  const scrollToTop = usePageScrollToTop()
  useEffect(() => {
    scrollToTop()
  }, [activeStep, scrollToTop])

  // Fleet dropdowns (Step 2) + save scope: the customer's org on-behalf, else the
  // current user's org. The backend ignores org_id for non-CAI users anyway.
  const fleetOrgId = orgId ?? user?.currentOrg.id

  return (
    <>
      <Helmet>
        <title>Report a claim</title>
      </Helmet>

      <div className="mx-auto flex w-full max-w-6xl flex-col md:flex-row md:gap-8">
        <aside className="hidden shrink-0 md:sticky md:top-[calc(var(--app-top-bar-height)+1rem)] md:block md:w-40 md:self-start md:px-4 md:py-10">
          <ClaimStepper
            activeStep={activeStep}
            completedSteps={Array.from({ length: activeStep - 1 }, (_, i) => i + 1)}
          />
        </aside>

        <div className="min-w-0 flex-1">
          {activeStep === 1 ? (
            <ClaimPhotosForm
              claimId={claimId}
              orgId={orgId}
              userId={userId}
              claim={claimData}
              reporterName={reporterName}
              reporterPhone={reporterPhone}
              onCreated={setClaimId}
              onSaved={(c) => {
                setClaimData(c)
                setActiveStep(2)
              }}
            />
          ) : activeStep === 2 ? (
            <ClaimTruckDriverForm
              claimId={claimId!}
              orgId={fleetOrgId}
              claim={claimData}
              onBack={() => setActiveStep(1)}
              onSaved={(c) => {
                setClaimData(c)
                setActiveStep(3)
              }}
            />
          ) : activeStep === 3 ? (
            <ClaimIncidentDetailsForm
              claimId={claimId!}
              orgId={orgId}
              claim={claimData}
              onBack={() => setActiveStep(2)}
              onSaved={(c) => {
                setClaimData(c)
                setActiveStep(4)
              }}
            />
          ) : activeStep === 4 ? (
            <ClaimOthersInvolvedForm
              claimId={claimId!}
              orgId={orgId}
              claim={claimData}
              onBack={() => setActiveStep(3)}
              onSaved={(c) => {
                setClaimData(c)
                setActiveStep(5)
              }}
            />
          ) : (
            <ClaimReviewForm claimId={claimId!} orgId={orgId} onBack={() => setActiveStep(4)} />
          )}
        </div>
      </div>
    </>
  )
}
